import { useRoute } from "vue-router";
import { inject, onMounted, ref, watch } from "vue";

import { useCaseStore } from "@/stores/case-store";
import { useBrandStore } from "@/stores/brand-store";
import { useCaseFileStore } from "@/stores/case-file-store";

import type { InjectionKey, Ref } from "vue";

export const CASE_ROUTE_PARAMS_KEY = Symbol() as InjectionKey<Ref<InjectedCaseRouteParams>>;

const ERROR = new Error("This hook should only be used in the Case Routes");

export function useCaseRouteParams() {
  const caseStore = useCaseStore();
  const caseFileStore = useCaseFileStore();

  const route = useRoute();
  const fallback = ref<InjectedCaseRouteParams>({} as never);

  watch(
    () => route.params,
    () => {
      const selectedCaseName = caseStore.selectedCaseName;
      const selectedCaseFile = caseFileStore.selectedCaseFile;
      const { selectedBrand, selectedClient } = useBrandStore();

      if (selectedClient == null || selectedBrand == null || selectedCaseName == null) {
        fallback.value = {} as never;
        return;
      }

      fallback.value = {
        ...route.params,
        client: selectedClient,
        brand: selectedBrand,
        casename: selectedCaseName,
        casefile: selectedCaseFile,
      };
    },
    { immediate: true },
  );

  onMounted(() => {
    const selectedCaseName = caseStore.selectedCaseName;
    const { selectedBrand, selectedClient } = useBrandStore();

    if (selectedClient == null || selectedBrand == null || selectedCaseName == null) throw ERROR;
  });

  return inject(CASE_ROUTE_PARAMS_KEY, fallback);
}

export type InjectedCaseRouteParams = CaseRouteParams & { client: string };

export interface CaseRouteParams {
  brand: string;
  casename: string;
  casefile?: string;
  annotation?: string;
  filepage?: string;
  snippet?: string;
  time?: string;
  startTime?: string;
  endTime?: string;
  fieldPath?: string;
}
